// src/components/TaskDetail.js - Fixed version
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTasks, updateTaskStatus } from '../features/tasks/taskSlice';
import { FaArrowLeft, FaCheck, FaEdit, FaTrash, FaClock, FaFlag } from 'react-icons/fa';

const TaskDetail = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { tasks } = useSelector(state => state.tasks);
    const [task, setTask] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        // Find the task from Redux state
        const foundTask = tasks.find(t => t.id === taskId);
        if (foundTask) {
            setTask(foundTask);
        } else {
            // If not in Redux, fetch all tasks
            dispatch(fetchTasks());
        }
    }, [taskId, tasks, dispatch]);

    const handleMarkCompleted = async () => {
        if (!task) return;

        setIsUpdating(true);
        try {
            await dispatch(updateTaskStatus({
                taskId: task.id,
                status: 'completed'
            })).unwrap();

            // Update local state
            setTask(prev => ({ ...prev, status: 'completed' }));

            alert('Task marked as completed!');
        } catch (error) {
            alert('Failed to update task: ' + error.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return '#dc3545'; // High priority - red
        if (score >= 6) return '#ffc107'; // Medium priority - yellow
        return '#28a745'; // Low priority - green
    };

    const getPriorityLabel = (score) => {
        if (score >= 8) return 'High Priority';
        if (score >= 6) return 'Medium Priority';
        return 'Low Priority';
    };

    // Safe status display function
    const getStatusDisplay = (status) => {
        if (!status) return 'PENDING';
        return status.toString().toUpperCase();
    };

    // Safe status class function
    const getStatusClass = (status) => {
        if (!status) return 'pending';
        return status.toString().toLowerCase();
    };

    if (!task) {
        return (
            <div className="task-detail-container">
                <div className="loading">
                    <div className="loading-spinner"></div>
                    <p>Loading task details...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="task-detail-container">
            <div className="task-detail-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FaArrowLeft /> Back
                </button>
                <div className="task-actions">
                    {getStatusClass(task.status) !== 'completed' && (
                        <button
                            className="btn btn-success"
                            onClick={handleMarkCompleted}
                            disabled={isUpdating}
                        >
                            <FaCheck /> {isUpdating ? 'Updating...' : 'Mark Completed'}
                        </button>
                    )}
                    <button className="btn btn-primary" onClick={() => navigate(`/tasks/${taskId}/edit`)}>
                        <FaEdit /> Edit Task
                    </button>
                </div>
            </div>

            <div className="task-detail-content">
                <div className="task-header-info">
                    <h1>{task.title || 'Untitled Task'}</h1>
                    <div className="task-status-badges">
                        <span className={`status-badge ${getStatusClass(task.status)}`}>
                            {getStatusDisplay(task.status)}
                        </span>
                        <span
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(task.priority_score || 0) }}
                        >
                            <FaFlag /> {getPriorityLabel(task.priority_score || 0)}
                        </span>
                    </div>
                </div>

                <div className="task-description">
                    <h2>Description</h2>
                    <p>{task.description || 'No description provided.'}</p>
                </div>

                <div className="task-metrics">
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className="metric-label">Priority Score</div>
                            <div className="metric-value">{task.priority_score || 0}/10</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Importance</div>
                            <div className="metric-value">{task.importance || 0}/10</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Urgency</div>
                            <div className="metric-value">{task.urgency || 0}/10</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Complexity</div>
                            <div className="metric-value">{task.complexity || 0}/10</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Estimated Hours</div>
                            <div className="metric-value">
                                <FaClock /> {task.estimated_hours || 0}h
                            </div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Assignee</div>
                            <div className="metric-value">{task.assignee || 'Unassigned'}</div>
                        </div>
                    </div>
                </div>

                {task.potential_blockers && Array.isArray(task.potential_blockers) && task.potential_blockers.length > 0 && (
                    <div className="task-blockers">
                        <h2>Potential Blockers</h2>
                        <ul>
                            {task.potential_blockers.map((blocker, index) => (
                                <li key={index}>{blocker}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="task-meta">
                    <div className="meta-info">
                        <strong>Created:</strong> {task.created_at ? new Date(task.created_at).toLocaleString() : 'Unknown'}
                    </div>
                    {task.due_date && (
                        <div className="meta-info">
                            <strong>Due Date:</strong> {new Date(task.due_date).toLocaleString()}
                        </div>
                    )}
                    {task.completed_at && (
                        <div className="meta-info">
                            <strong>Completed:</strong> {new Date(task.completed_at).toLocaleString()}
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .task-detail-container {
                    max-width: 1000px;
                    margin: 0 auto;
                    padding: 20px;
                }

                .loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    background: white;
                    border-radius: 16px;
                    margin-bottom: 30px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                }

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid #667eea;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .task-detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 30px;
                    padding-bottom: 20px;
                    border-bottom: 2px solid #e9ecef;
                }

                .back-button {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #6c757d;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: background 0.3s;
                }

                .back-button:hover {
                    background: #545b62;
                }

                .task-actions {
                    display: flex;
                    gap: 10px;
                }

                .btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-weight: 600;
                    transition: all 0.3s;
                    text-decoration: none;
                }

                .btn-success {
                    background: #28a745;
                    color: white;
                }

                .btn-success:hover {
                    background: #218838;
                }

                .btn-primary {
                    background: #007bff;
                    color: white;
                }

                .btn-primary:hover {
                    background: #0056b3;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .task-detail-content {
                    background: white;
                    border-radius: 12px;
                    padding: 30px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
                }

                .task-header-info {
                    margin-bottom: 30px;
                }

                .task-header-info h1 {
                    margin: 0 0 15px 0;
                    color: #333;
                    font-size: 2rem;
                    word-wrap: break-word;
                }

                .task-status-badges {
                    display: flex;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .status-badge {
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: bold;
                    text-transform: uppercase;
                }

                .status-badge.pending {
                    background: #ffc107;
                    color: #212529;
                }

                .status-badge.completed {
                    background: #28a745;
                    color: white;
                }

                .status-badge.in_progress {
                    background: #007bff;
                    color: white;
                }

                .priority-badge {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    padding: 5px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: bold;
                    color: white;
                }

                .task-description {
                    margin-bottom: 30px;
                }

                .task-description h2 {
                    margin: 0 0 15px 0;
                    color: #495057;
                    font-size: 1.3rem;
                }

                .task-description p {
                    line-height: 1.6;
                    color: #6c757d;
                    font-size: 16px;
                    white-space: pre-wrap;
                    word-wrap: break-word;
                }

                .task-metrics {
                    margin-bottom: 30px;
                }

                .metrics-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                }

                .metric-card {
                    background: #f8f9fa;
                    padding: 20px;
                    border-radius: 8px;
                    text-align: center;
                    border: 2px solid #e9ecef;
                }

                .metric-label {
                    font-size: 12px;
                    color: #6c757d;
                    text-transform: uppercase;
                    font-weight: 600;
                    margin-bottom: 8px;
                }

                .metric-value {
                    font-size: 24px;
                    font-weight: bold;
                    color: #495057;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }

                .task-blockers {
                    margin-bottom: 30px;
                }

                .task-blockers h2 {
                    margin: 0 0 15px 0;
                    color: #495057;
                    font-size: 1.3rem;
                }

                .task-blockers ul {
                    list-style: none;
                    padding: 0;
                }

                .task-blockers li {
                    background: #fff3cd;
                    border: 1px solid #ffeaa7;
                    border-radius: 6px;
                    padding: 12px;
                    margin-bottom: 8px;
                    color: #856404;
                }

                .task-meta {
                    border-top: 1px solid #e9ecef;
                    padding-top: 20px;
                    display: flex;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .meta-info {
                    color: #6c757d;
                    font-size: 14px;
                }

                @media (max-width: 768px) {
                    .task-detail-header {
                        flex-direction: column;
                        gap: 15px;
                        align-items: flex-start;
                    }

                    .task-actions {
                        width: 100%;
                        justify-content: flex-end;
                    }

                    .metrics-grid {
                        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                        gap: 15px;
                    }

                    .task-meta {
                        flex-direction: column;
                        gap: 10px;
                    }
                }
            `}</style>
        </div>
    );
};

export default TaskDetail;